import { Skeleton, Stack, TextField, TextFieldProps } from "@mui/material";
import { FieldValues } from "react-hook-form";
import FormFieldHint from "./FormFieldHint";

interface Props extends FieldValues, Omit<TextFieldProps, "name" | "label" | "control"> {
	label: string;
	value: string;
	isLoading?: boolean;
	hintMessage?: string;
}

/**
 * FormReadOnlyField acts as a wrapper component for text fields used in read only mode in conjunction with React Hook Form.
 *
 * @note use this component only when a value should be presented to the end user but no form is available.
 *
 * @param value       the field value
 * @param label       the label of the text field
 * @param hintMessage the optional hint message
 * @param isLoading   if true, the component will render a skeleton element
 * @param rest
 * @returns           a read only TextField wrapped in a React Hook Form Control component
 */
const FormReadOnlyField = ({ label, value, isLoading, hintMessage, ...rest }: Props): React.ReactElement => {
	if (isLoading) {
		return <Skeleton height="44px" variant="rounded" className="loading-field" />;
	}

	return (
		<Stack spacing={1} direction="row">
			<TextField
				{...rest}
				value={value ?? ""}
				size="small"
				label={label}
				variant="filled"
				helperText={hintMessage ?? <FormFieldHint text={hintMessage} />}
				slotProps={{ input: { readOnly: true } }}
				className="read-only"
			/>
		</Stack>
	);
};

export default FormReadOnlyField;
